"use client"

import { useState, useEffect } from "react"

const FinancialInsights = () => {
  const [activeInsight, setActiveInsight] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  const insights = [
    {
      id: 1,
      title: "Investment Mistakes",
      subtitle: "Top 5 Critical Errors",
      description:
        "Many beginners make common mistakes like chasing quick returns, ignoring diversification, or not doing proper research. In this guide, we break down the most frequent investment errors and show you how to avoid them—so you can grow your wealth with confidence.",
      icon: "⚠️",
      color: "#FF6B6B",
      gradient: "linear-gradient(135deg, #FF6B6B, #FF8E8E)",
      category: "Risk Management",
      readTime: "5 min read",
      difficulty: "Beginner",
    },
    {
      id: 2,
      title: "Retirement Fund",
      subtitle: "Build Your Future",
      description:
        "Planning for retirement early is one of the best financial decisions you can make. Learn how to set clear goals, choose the right investment options, and build a secure fund that ensures financial freedom in your later years.",
      icon: "🏦",
      color: "#4ECDC4",
      gradient: "linear-gradient(135deg, #4ECDC4, #6EE7DD)",
      category: "Long-term Planning",
      readTime: "8 min read",
      difficulty: "Intermediate",
    },
    {
      id: 3,
      title: "Mutual Funds",
      subtitle: "Smart Investment Choice",
      description:
        "If you're new to investing, mutual funds can be a great way to get started. This beginner-friendly guide explains what mutual funds are, how they work, and why they're a smart choice for both short-term and long-term financial growth.",
      icon: "📈",
      color: "#45B7D1",
      gradient: "linear-gradient(135deg, #45B7D1, #6BC5E8)",
      category: "Investment Basics",
      readTime: "6 min read",
      difficulty: "Beginner",
    },
    {
      id: 4,
      title: "Tax Strategies",
      subtitle: "Maximize Your Savings",
      description:
        "Why pay more tax than necessary? Discover effective and legal ways to save money on taxes in 2026, including investment-linked deductions, retirement benefits, and smart financial planning tips tailored to the latest tax rules.",
      icon: "💰",
      color: "#96CEB4",
      gradient: "linear-gradient(135deg, #96CEB4, #B8E6C1)",
      category: "Tax Planning",
      readTime: "7 min read",
      difficulty: "Advanced",
    },
    {
      id: 5,
      title: "Portfolio Management",
      subtitle: "Optimize Your Assets",
      description:
        "Learn advanced portfolio management techniques to maximize returns while minimizing risks. Discover asset allocation strategies, rebalancing methods, and performance tracking tools.",
      icon: "📊",
      color: "#FFEAA7",
      gradient: "linear-gradient(135deg, #FFEAA7, #FFF2C7)",
      category: "Advanced Strategy",
      readTime: "10 min read",
      difficulty: "Advanced",
    },
    {
      id: 6,
      title: "Emergency Fund",
      subtitle: "Financial Safety Net",
      description:
        "Build a robust emergency fund that protects you from unexpected financial setbacks. Learn how much to save, where to keep it, and when to use it without compromising your long-term goals.",
      icon: "🛡️",
      color: "#DDA0DD",
      gradient: "linear-gradient(135deg, #DDA0DD, #E8B8E8)",
      category: "Financial Security",
      readTime: "4 min read",
      difficulty: "Beginner",
    },
  ]

  const handleInsightChange = (index) => {
    setIsLoading(true)
    setTimeout(() => {
      setActiveInsight(index)
      setIsLoading(false)
    }, 300)
  }

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveInsight((prev) => (prev + 1) % insights.length)
    }, 8000)

    return () => clearInterval(interval)
  }, [insights.length])

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "white",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated Background Elements */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
          radial-gradient(circle at ${Math.sin(Date.now() / 3000) * 50 + 50}% ${Math.cos(Date.now() / 4000) * 50 + 50}%, 
          ${insights[activeInsight].color}08 0%, transparent 50%)
        `,
          transition: "all 2s ease-in-out",
          zIndex: 1,
        }}
      />

      <div className="container-fluid" style={{ position: "relative", zIndex: 2 }}>
        <div className="row justify-content-center pt-5">
          <div className="col-12 text-center mb-5">
            <div
              style={{
                background: "rgba(248, 249, 250, 0.95)",
                backdropFilter: "blur(20px)",
                borderRadius: "25px",
                padding: "30px 50px",
                display: "inline-block",
                border: `2px solid ${insights[activeInsight].color}30`,
                boxShadow: `0 20px 40px ${insights[activeInsight].color}20`,
                transition: "all 0.6s ease",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "15px",
                }}
              >
                <div
                  style={{
                    width: "60px",
                    height: "60px",
                    background: insights[activeInsight].gradient,
                    borderRadius: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "2rem",
                    marginRight: "20px",
                    transform: "rotate(5deg)",
                    boxShadow: `0 10px 25px ${insights[activeInsight].color}40`,
                  }}
                >
                  💼
                </div>
                <div>
                  <h1
                    style={{
                      fontSize: "3rem",
                      fontWeight: "800",
                      color: "black",
                      margin: 0,
                      lineHeight: "1.2",
                    }}
                  >
                    Financial Insights Hub
                  </h1>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "15px",
                      marginTop: "10px",
                    }}
                  >
                    <span
                      style={{
                        background: insights[activeInsight].gradient,
                        color: "white",
                        padding: "5px 15px",
                        borderRadius: "20px",
                        fontSize: "1.2rem",
                        fontWeight: "600",
                      }}
                    >
                      {insights.length} Expert Insights
                    </span>
                  </div>
                </div>
              </div>
              <p
                style={{
                  fontSize: "1.1rem",
                  color: "rgba(0, 0, 0, 0.7)",
                  maxWidth: "600px",
                  margin: "0 auto",
                  lineHeight: "1.6",
                }}
              >
                Navigate through our curated collection of financial wisdom. Discover expert insights to grow your
                wealth.
              </p>
            </div>
          </div>
        </div>

        <div className="row justify-content-center py-5">
          <div className="col-12 col-xl-10">
            <div className="row g-4">
              <div className="col-12 col-lg-8">
                <div
                  style={{
                    background: "rgba(248, 249, 250, 0.95)",
                    backdropFilter: "blur(20px)",
                    borderRadius: "25px",
                    padding: "40px",
                    border: `2px solid ${insights[activeInsight].color}30`,
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: `0 30px 60px ${insights[activeInsight].color}20`,
                    transition: "all 0.6s ease",
                    minHeight: "500px",
                  }}
                >
                  {/* Animated Border */}
                  <div
                    style={{
                      position: "absolute",
                      top: "-2px",
                      left: "-2px",
                      right: "-2px",
                      bottom: "-2px",
                      background: `conic-gradient(from 0deg, ${insights[activeInsight].color}, transparent, ${insights[activeInsight].color})`,
                      borderRadius: "27px",
                      zIndex: -1,
                      opacity: 0.3,
                      animation: "rotate 8s linear infinite",
                    }}
                  />

                  {/* Loading Overlay */}
                  {isLoading && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "rgba(255, 255, 255, 0.9)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "25px",
                        zIndex: 10,
                      }}
                    >
                      <div
                        style={{
                          width: "50px",
                          height: "50px",
                          border: `4px solid ${insights[activeInsight].color}30`,
                          borderTop: `4px solid ${insights[activeInsight].color}`,
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                        }}
                      />
                    </div>
                  )}

                  <div className="row align-items-center h-100">
                    <div className="col-12 col-md-8">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "25px",
                        }}
                      >
                        <div
                          style={{
                            width: "80px",
                            height: "80px",
                            background: insights[activeInsight].gradient,
                            borderRadius: "20px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginRight: "20px",
                            fontSize: "2.5rem",
                            boxShadow: `0 15px 30px ${insights[activeInsight].color}40`,
                            transform: "rotate(-5deg)",
                          }}
                        >
                          {insights[activeInsight].icon}
                        </div>
                        <div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              marginBottom: "8px",
                            }}
                          >
                            <span
                              style={{
                                background: insights[activeInsight].gradient,
                                color: "white",
                                padding: "4px 12px",
                                borderRadius: "15px",
                                fontSize: "1rem",
                                fontWeight: "600",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                              }}
                            >
                              {insights[activeInsight].category}
                            </span>
                          </div>
                          <h2
                            style={{
                              fontSize: "2.2rem",
                              fontWeight: "800",
                              color: "black",
                              margin: 0,
                              lineHeight: "1.2",
                            }}
                          >
                            {insights[activeInsight].title}
                          </h2>
                          <h3
                            style={{
                              fontSize: "1.3rem",
                              fontWeight: "600",
                              color: "rgba(0, 0, 0, 0.6)",
                              margin: 0,
                            }}
                          >
                            {insights[activeInsight].subtitle}
                          </h3>
                        </div>
                      </div>

                      <p
                        style={{
                          fontSize: "1.5rem",
                          color: "rgba(0, 0, 0, 0.7)",
                          lineHeight: "1.7",
                          marginBottom: "30px",
                        }}
                      >
                        {insights[activeInsight].description}
                      </p>

                      {/* <div
                        style={{
                          display: "flex",
                          gap: "15px",
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <button
                          style={{
                            background: insights[activeInsight].gradient,
                            border: "none",
                            borderRadius: "15px",
                            padding: "15px 30px",
                            color: "white",
                            fontSize: "1.2rem",
                            fontWeight: "700",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            boxShadow: `0 10px 25px ${insights[activeInsight].color}40`,
                            transform: "translateY(0)",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = "translateY(-3px) scale(1.05)"
                            e.target.style.boxShadow = `0 15px 35px ${insights[activeInsight].color}60`
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = "translateY(0) scale(1)"
                            e.target.style.boxShadow = `0 10px 25px ${insights[activeInsight].color}40`
                          }}
                        >
                          Read Full Guide →
                        </button>

                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <span
                            style={{
                              color: "rgba(0, 0, 0, 0.5)",
                              fontSize: "1.2rem",
                            }}
                          >
                            Difficulty:
                          </span>
                          <span
                            style={{
                              background: `${insights[activeInsight].color}20`,
                              color: insights[activeInsight].color,
                              padding: "4px 12px",
                              borderRadius: "12px",
                              fontSize: "1.2rem",
                              fontWeight: "600",
                            }}
                          >
                            {insights[activeInsight].difficulty}
                          </span>
                        </div>
                      </div> */}
                    </div>

                    <div className="col-12 col-md-4 text-center">
                      <div
                        style={{
                          width: "200px",
                          height: "200px",
                          background: `conic-gradient(from 0deg, ${insights[activeInsight].color}40, ${insights[activeInsight].color}10)`,
                          borderRadius: "50%",
                          margin: "0 auto",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "4rem",
                          animation: "float 6s ease-in-out infinite",
                          boxShadow: `0 20px 40px ${insights[activeInsight].color}30`,
                        }}
                      >
                        {insights[activeInsight].icon}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 col-lg-4">
                <div
                  style={{
                    background: "rgba(248, 249, 250, 0.95)",
                    backdropFilter: "blur(20px)",
                    borderRadius: "25px",
                    padding: "30px",
                    border: "1px solid rgba(0, 0, 0, 0.1)",
                    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
                    height: "fit-content",
                    position: "sticky",
                    top: "20px",
                  }}
                >
                  <h3
                    style={{
                      color: "black",
                      fontSize: "1.5rem",
                      fontWeight: "700",
                      marginBottom: "25px",
                      textAlign: "center",
                    }}
                  >
                    Quick Navigation
                  </h3>

                  <div style={{ marginBottom: "25px" }}>
                    {insights.map((insight, index) => (
                      <div
                        key={insight.id}
                        onClick={() => handleInsightChange(index)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "15px",
                          marginBottom: "10px",
                          borderRadius: "15px",
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                          background: index === activeInsight ? `${insight.color}20` : "transparent",
                          border: index === activeInsight ? `2px solid ${insight.color}60` : "2px solid transparent",
                          transform: index === activeInsight ? "scale(1.02)" : "scale(1)",
                        }}
                        onMouseEnter={(e) => {
                          if (index !== activeInsight) {
                            e.currentTarget.style.background = `${insight.color}10`
                            e.currentTarget.style.transform = "scale(1.01)"
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (index !== activeInsight) {
                            e.currentTarget.style.background = "transparent"
                            e.currentTarget.style.transform = "scale(1)"
                          }
                        }}
                      >
                        <div
                          style={{
                            width: "40px",
                            height: "40px",
                            background: insight.gradient,
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "2rem",
                            marginRight: "15px",
                            flexShrink: 0,
                          }}
                        >
                          {insight.icon}
                        </div>
                        <div style={{ flex: 1 }}>
                          <h4
                            style={{
                              color: "black",
                              fontSize: "1.5rem",
                              fontWeight: "700",
                              margin: 0,
                              lineHeight: "1.2",
                            }}
                          >
                            {insight.title}
                          </h4>
                          <p
                            style={{
                              color: "rgba(0, 0, 0, 0.6)",
                              fontSize: "1rem",
                              margin: 0,
                            }}
                          >
                            {insight.category}
                          </p>
                        </div>
                        {index === activeInsight && (
                          <div
                            style={{
                              width: "8px",
                              height: "8px",
                              background: insight.color,
                              borderRadius: "50%",
                              marginLeft: "10px",
                              animation: "pulse 2s infinite",
                            }}
                          />
                        )}
                      </div>
                    ))}
                  </div>

                  {/* <div
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      background: `${insights[activeInsight].color}10`,
                      borderRadius: "15px",
                      border: `1px solid ${insights[activeInsight].color}30`,
                    }}
                  >
                    <h4
                      style={{
                        color: "black",
                        fontSize: "1rem",
                        fontWeight: "700",
                        marginBottom: "10px",
                      }}
                    >
                      Progress Tracker
                    </h4>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "15px",
                      }}
                    >
                      <span
                        style={{
                          color: "rgba(0, 0, 0, 0.6)",
                          fontSize: "0.8rem",
                        }}
                      >
                        {activeInsight + 1} of {insights.length}
                      </span>
                      <span
                        style={{
                          color: insights[activeInsight].color,
                          fontSize: "0.8rem",
                          fontWeight: "600",
                        }}
                      >
                        {Math.round(((activeInsight + 1) / insights.length) * 100)}%
                      </span>
                    </div>
                    <div
                      style={{
                        width: "100%",
                        height: "8px",
                        background: "rgba(0, 0, 0, 0.1)",
                        borderRadius: "4px",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${((activeInsight + 1) / insights.length) * 100}%`,
                          height: "100%",
                          background: insights[activeInsight].gradient,
                          borderRadius: "4px",
                          transition: "width 0.6s ease",
                        }}
                      />
                    </div>
                  </div> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FinancialInsights
